<template>
  <div
    class="custom-editor-input"
    @click="onTriggerFocus"
  >
    <EditorContent
      :id="id"
      :editor="editor"
      class="custom-editor-input__in"
      :class="{ 'is-error': isError }"
    />
    <slot
      name="footer"
      :editor="editor"
    />
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Emit, Prop, Watch } from "vue-property-decorator"
import Placeholder from "@tiptap/extension-placeholder"
import Table from "@tiptap/extension-table"
import TableCell from "@tiptap/extension-table-cell"
import TableHeader from "@tiptap/extension-table-header"
import TableRow from "@tiptap/extension-table-row"
import TextAlign from "@tiptap/extension-text-align"
import TextStyle from "@tiptap/extension-text-style"
import Underline from "@tiptap/extension-underline"
import StarterKit from "@tiptap/starter-kit"
import { Editor, EditorContent, Extension } from "@tiptap/vue-3"

import EditorMenuBar from "@/components/Editor/EditorMenuBar.vue"

import { TypeConfig } from "@/models"
import { EditorData } from "@/models/EditorData"
import { ClearStoredLink } from "@/plugins/extensions/ClearStoredLink"
import { Color } from "@/plugins/extensions/Color"
import { CustomEmoji } from "@/plugins/extensions/CustomEmoji"
import { NonInclusiveLink } from "@/plugins/extensions/NonInclusiveLink"
import { PasteColorSanitizer } from "@/plugins/extensions/PasteColorSanitizer"
import { PasteFile } from "@/plugins/extensions/PasteFile"
import { ReferenceAllUser, ReferenceAt } from "@/plugins/extensions/reference/referenceAt"
import {
  ReferenceHashEpic,
  ReferenceHashIdea,
  ReferenceHashItem,
  ReferenceHashMilestone,
  ReferenceHashProject,
  ReferenceHashSprint,
  ReferenceHashTask,
  ReferenceHashTeam,
  ReferenceHashTip
} from "@/plugins/extensions/reference/referenceHash"
import { ReferenceLink } from "@/plugins/extensions/reference/referenceLink"
import { StarterKitCustomHeading } from "@/plugins/extensions/StarterKitCustomHeading"

import { InitComponent } from "../Reference"
import EditorAttachment from "./EditorAttachment.vue"

@Options({
  name: "EditorInput",
  components: {
    EditorContent,
    EditorMenuBar,
    PasteFile
  }
})
export default class EditorComponent extends Vue {
  @Prop({
    type: String,
    default: ""
  })
  readonly id!: string
  @Prop({
    type: String,
    default: ""
  })
  value: string
  @Prop({
    type: String,
    default: ""
  })
  jsonValue: string
  @Prop() targetConfig: TypeConfig | null
  @Prop({ default: null }) referenceFileTargetConfig: TypeConfig | null
  @Prop() file: { url: string; type: string }
  @Prop({ default: false }) readonly readonly: boolean
  @Prop({ default: false }) readonly isError: boolean
  @Prop({ default: "" }) readonly placeholder: string
  @Prop({ default: false }) readonly focusEditor: boolean
  @Prop({ default: false }) readonly focusEnd: boolean
  @Prop({ default: false }) readonly triggerFocus: boolean
  @Prop({ default: false }) readonly triggerHandleInputOnMount: boolean
  @Prop({ default: true }) readonly referencesEnabled: boolean
  @Prop({ default: [] }) customExtensions: Extension[]

  editor: Editor | null = null
  customKeyboardShortcuts = Extension.create({})
  editorData: EditorData
  didFocusEnd = false

  mounted() {
    this.init()
    this.$nextTick(() => {
      this.focus()
    })
  }

  @Watch("focusEditor")
  focus() {
    if (this.focusEditor) {
      if (this.focusEnd && !this.didFocusEnd) {
        this.didFocusEnd = true
        this.editor?.commands.focus("end")
      } else {
        this.editor?.commands.focus()
      }
    }
  }

  @Watch("triggerFocus")
  onTriggerFocus() {
    this.focus()
  }

  init() {
    this.editorData = new EditorData({
      USER: InitComponent,
      PROJECT: InitComponent,
      EPIC: InitComponent,
      IDEA: InitComponent,
      MILESTONE: InitComponent,
      TEAM: InitComponent,
      SPRINT: InitComponent,
      ITEM: InitComponent,
      TASK: InitComponent,
      ATTACHMENT: EditorAttachment
    })
    const extensions = [
      StarterKitCustomHeading.configure({
        levels: [1, 2]
      }),
      StarterKit.configure({
        heading: false
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"]
      }),
      NonInclusiveLink.configure({
        HTMLAttributes: {
          class: "link is-medium"
        },
        validate: href => /^https?:\/\//.test(href)
      }),
      ClearStoredLink,
      Underline,
      Placeholder.configure({
        placeholder: this.placeholder
      }),
      ...(this.referencesEnabled
        ? [
            ReferenceAt,
            ReferenceAllUser,
            ReferenceHashProject,
            ReferenceHashMilestone,
            ReferenceHashEpic,
            ReferenceHashIdea,
            ReferenceHashTeam,
            ReferenceHashSprint,
            ReferenceHashItem,
            ReferenceHashTask,
            ReferenceHashTip,
            ReferenceLink
          ]
        : []),
      PasteFile(this.onPasteFile),
      PasteColorSanitizer,
      CustomEmoji,
      Table.configure({
        resizable: true
      }),
      TableRow,
      TableHeader,
      TableCell,
      TextStyle,
      Color
    ]

    const mergedExtensions = [...extensions, ...this.customExtensions].filter(
      (extension, index, allExtensions) =>
        index === allExtensions.findIndex(({ name }) => name === extension.name)
    )

    this.editor = new Editor({
      extensions: mergedExtensions,
      content: this.value,
      onUpdate: () => {
        this.handleInput()
      },
      onFocus: event => {
        this.$emit("focus", {
          event: event
        })
      },
      onBlur: event => {
        this.$emit("blur", {
          event: event
        })
      }
    })
    this.onReferenceFileTargetConfigChange()

    if (this.triggerHandleInputOnMount) {
      this.handleInput()
    }

    this.onSetEditable()
  }

  @Watch("readonly")
  onSetEditable() {
    if (!this.editor) return

    this.readonly && this.editor ? this.editor.setEditable(false) : this.editor.setEditable(true)
  }

  @Watch("referenceFileTargetConfig", { deep: true })
  onReferenceFileTargetConfigChange() {
    if (!this.editor) return

    Object.assign(this.editor, {
      referenceFileTargetConfig: this.referenceFileTargetConfig
    })
  }

  @Watch("value")
  onValueChanged(value: string) {
    if (this.editor) {
      const currentHtml = this.editor.getHTML()
      if (currentHtml === value) return

      if (this.jsonValue) {
        const jsonContent = JSON.parse(this.jsonValue)
        this.editor.commands.setContent(jsonContent, false)
      } else {
        this.editor.commands.setContent(this.value, false)
      }
    }
  }

  @Watch("jsonValue")
  onJsonValueChanged(jsonValue: string) {
    if (this.editor && jsonValue) {
      const jsonContent = JSON.parse(jsonValue)
      const currentJson = JSON.stringify(this.editor.getJSON())
      if (currentJson === jsonValue) return
      this.editor.commands.setContent(jsonContent, false)
    }
  }

  @Emit("handleInput")
  handleInput() {
    if (this.editor) {
      this.editorData.setText(this.editor.getText())
      this.editorData.setJson(this.editor.getJSON())
      this.editorData.setHtml(this.editor.getHTML())
      return this.editorData
    }
  }

  beforeUnmount() {
    this.editor?.destroy()
  }

  @Emit()
  onPasteFile(file: File) {
    return file
  }
}
</script>
